/**
 * The static-catalog cache, on disk in the browser.
 *
 * **This schema is byte-compatible with RinNET's on purpose and must stay that way.**
 * Same database name, same version, same stores, same key paths, same indexes. The two
 * portals talk to the same server, so a user who has already downloaded the song lists
 * in one keeps them in the other. Change any of this and every existing user silently
 * re-downloads several megabytes of catalog on their next visit.
 *
 * That constraint is why the `chusanMusic` index below is not fixed. See the comment
 * on it before you "correct" anything here.
 */

export const DB_NAME = 'Aqua';
export const DB_VERSION = 6;

export const STORE_NAMES = [
  'ongekiCard',
  'ongekiCharacter',
  'ongekiMusic',
  'ongekiSkill',
  'ongekiTrophy',
  'chusanMusic',
  'chusanCharacter',
  'chusanTrophy',
  'chusanNamePlate',
  'chusanSystemVoice',
  'chusanMapIcon',
  'chusanFrame',
  'chusanAvatarAcc',
  'chusanSymbolChat',
  'chusanStage',
  'maimai2Music',
] as const;

export type StoreName = (typeof STORE_NAMES)[number];

export interface IndexDef {
  /** The name the index is looked up by. */
  name: string;
  /** The property it actually indexes, which is not always the same word. */
  keyPath: string;
}

export interface StoreSchema {
  keyPath: string;
  indexes: readonly IndexDef[];
}

const named = (...names: string[]): IndexDef[] => names.map((name) => ({ name, keyPath: name }));

export const STORE_SCHEMAS: Record<StoreName, StoreSchema> = {
  ongekiCard: {
    keyPath: 'id',
    indexes: named(
      'name',
      'nickName',
      'attribute',
      'charaId',
      'school',
      'gakuen',
      'rarity',
      'levelParam',
      'skillId',
      'chouKaikaSkillId',
      'cardNumber',
      'version',
    ),
  },
  ongekiCharacter: { keyPath: 'id', indexes: named('name', 'cv', 'modelId') },
  ongekiMusic: {
    keyPath: 'id',
    indexes: named(
      'name',
      'sortName',
      'artistName',
      'genre',
      'bossCardId',
      'bossLevel',
      'level0',
      'level1',
      'level2',
      'level3',
      'level4',
    ),
  },
  ongekiSkill: { keyPath: 'id', indexes: named('name', 'sortName', 'category', 'info') },
  ongekiTrophy: { keyPath: 'id', indexes: named('name', 'rarityType') },
  chusanMusic: {
    keyPath: 'musicId',
    indexes: [
      { name: 'name', keyPath: 'name' },
      /**
       * The index is called `sortName`; the property it points at is `sotrName`.
       *
       * That typo is in every existing user's database. Fixing it would need
       * `DB_VERSION` bumped, and the upgrade would drop and rebuild the store — which
       * means every user re-downloads the whole CHUNITHM catalog to correct an index
       * nothing reads by name anyway. The typo is cheaper than the fix. Leave it.
       */
      { name: 'sortName', keyPath: 'sotrName' },
      { name: 'artistName', keyPath: 'artistName' },
      { name: 'genre', keyPath: 'genre' },
      { name: 'releaseVersion', keyPath: 'releaseVersion' },
    ],
  },
  chusanCharacter: {
    keyPath: 'id',
    indexes: named('name', 'releaseTag', 'worksName', 'illustratorName', 'addImages'),
  },
  chusanTrophy: { keyPath: 'id', indexes: named('name') },
  chusanNamePlate: { keyPath: 'id', indexes: named('name') },
  chusanSystemVoice: { keyPath: 'id', indexes: named('name') },
  chusanMapIcon: { keyPath: 'id', indexes: named('name') },
  chusanFrame: { keyPath: 'id', indexes: named('name') },
  chusanAvatarAcc: { keyPath: 'id', indexes: named('name', 'category') },
  chusanStage: { keyPath: 'id', indexes: named('name') },
  chusanSymbolChat: {
    keyPath: 'id',
    indexes: named('name', 'sortName', 'text', 'balloonId', 'sceneIds'),
  },
  maimai2Music: {
    keyPath: 'musicId',
    indexes: named(
      'musicId',
      'name',
      'sortName',
      'artistName',
      'genreId',
      'romVersion',
      'addVersion',
    ),
  },
};

/**
 * Stores an older database version created and this one does not.
 *
 * Dropped during upgrade so the browser reclaims the space. A fresh database never
 * contains them, so the deletion is guarded rather than unconditional.
 */
export const LEGACY_STORES = [
  'divaPv',
  'divaModule',
  'divaCustomize',
  'chuniMusic',
  'chuniCharacter',
  'chuniSkill',
] as const;
